import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Upload, FileText, FileSpreadsheet, X, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const CSVUploadModal = ({ onClose, onSuccess, token }) => {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx'))) {
      setFile(selectedFile);
    } else {
      toast.error('Please select a CSV or Excel (.xlsx) file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.xlsx'))) {
      setFile(droppedFile);
    } else {
      toast.error('Please drop a CSV or Excel (.xlsx) file');
    }
  };

  const handleDownloadTemplate = async (type) => {
    const endpoint = type === 'csv' ? 'download' : 'download-excel';
    const filename = type === 'csv' ? 'jurisdictions_template.csv' : 'jurisdictions_template.xlsx';
    try {
      const response = await fetch(`${API_BASE_URL}/api/jurisdictions/template/${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to download template');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      // Jun 14, 2026 — FILES-TOAST-001: removed "template downloaded" toast.
    } catch (err) {
      console.error('Error downloading template:', err);
      toast.error('Failed to download template');
    }
  };

  const handleValidate = async () => {
    if (!file) { toast.error('Please select a file first'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/jurisdictions/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Validation failed');
      }
      const result = await response.json();
      setValidationResult(result);
      setStep('preview');
    } catch (err) {
      console.error('Error validating CSV:', err);
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/jurisdictions/bulk-upload/confirm`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Import failed');
      }
      const result = await response.json();
      setImportResult(result);
      if (result.skipped_count > 0) {
        toast.warning(`Imported ${result.imported_count} jurisdictions; ${result.skipped_count} row${result.skipped_count !== 1 ? 's' : ''} skipped`);
      } else {
        toast.success(`Successfully imported ${result.imported_count} jurisdictions!`);
      }
      setStep('complete');
      if (!result.skipped_count) setTimeout(() => { onSuccess(); }, 1500);
    } catch (err) {
      console.error('Error importing CSV:', err);
      toast.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  // Inline styles for the compact modal
  const overlayStyle = {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px'
  };

  const modalStyle = {
    background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden'
  };

  const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid #e5e7eb'
  };

  const bodyStyle = { padding: '20px' };

  const dropZoneStyle = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: file ? '24px 20px' : '40px 20px',
    border: `2px dashed ${dragOver ? '#6366f1' : file ? '#22c55e' : '#cbd5e1'}`,
    borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s ease',
    background: dragOver ? '#f5f3ff' : file ? '#f0fdf4' : '#fafafa'
  };

  const templateBarStyle = {
    display: 'flex', gap: '8px', marginTop: '16px'
  };

  const footerStyle = {
    display: 'flex', justifyContent: 'flex-end', gap: '8px',
    padding: '12px 20px', borderTop: '1px solid #e5e7eb', background: '#fafafa'
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="bulk-upload-modal">

        {/* Header */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
            Bulk Upload Jurisdictions
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="bulk-upload-close-btn"
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={18} />
          </Button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>

          {step === 'upload' && (
            <>
              {/* Drop zone — primary focus */}
              <div
                style={dropZoneStyle}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                data-testid="file-drop-zone"
              >
                {file ? (
                  <>
                    <CheckCircle size={28} color="#22c55e" />
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginTop: '8px' }}>{file.name}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      {(file.size / 1024).toFixed(1)} KB &middot;
                      <span
                        style={{ color: '#6366f1', cursor: 'pointer', marginLeft: '4px', textDecoration: 'underline' }}
                        onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      >
                        Remove
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <Upload size={32} color={dragOver ? '#6366f1' : '#94a3b8'} />
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginTop: '10px' }}>
                      Drop your file here, or click to browse
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      Accepts .csv or .xlsx
                    </div>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                data-testid="csv-file-input"
              />

              {/* Template download buttons */}
              <div style={templateBarStyle}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadTemplate('csv')}
                  data-testid="download-csv-template-btn"
                  className="flex-1"
                >
                  <FileText size={14} /> CSV Template
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadTemplate('excel')}
                  data-testid="download-excel-template-btn"
                  className="flex-1"
                >
                  <FileSpreadsheet size={14} /> Excel Template
                </Button>
              </div>
            </>
          )}

          {step === 'preview' && validationResult && (
            <>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                {[
                  { label: 'Total', value: validationResult.total_rows, bg: '#f1f5f9', color: '#334155' },
                  { label: 'Valid', value: validationResult.valid_rows, bg: '#f0fdf4', color: '#166534' },
                  { label: 'Invalid', value: validationResult.invalid_rows, bg: '#fef2f2', color: '#991b1b' }
                ].map((s) => (
                  <div key={s.label} style={{ flex: 1, textAlign: 'center', padding: '12px 8px', background: s.bg, borderRadius: '8px' }}>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {validationResult.invalid_rows > 0 && (
                <div style={{ marginBottom: '14px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', maxHeight: '120px', overflowY: 'auto' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#991b1b', marginBottom: '6px' }}>Errors</div>
                  {validationResult.errors.slice(0, 5).map((error, idx) => (
                    <div key={idx} style={{ fontSize: '12px', color: '#7f1d1d', marginBottom: '4px' }}>
                      <strong>Row {error.row}:</strong> {error.errors.join(', ')}
                    </div>
                  ))}
                  {validationResult.errors.length > 5 && (
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                      +{validationResult.errors.length - 5} more
                    </div>
                  )}
                </div>
              )}

              {validationResult.valid_rows > 0 && (
                <div style={{ borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                  <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="tf-thead">
                        {['Type', 'Name', 'Make Checks Payable To', 'Tax Collector Name', 'Phone'].map((h) => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {validationResult.preview_data.slice(0, 5).map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '7px 10px', color: '#334155' }}>{row.jurisdiction_type}</td>
                          <td style={{ padding: '7px 10px', color: '#334155', fontWeight: '500' }}>{row.jurisdiction_name}</td>
                          <td style={{ padding: '7px 10px', color: '#334155' }} data-testid="csv-preview-payee">{row.make_checks_payable_to}</td>
                          <td style={{ padding: '7px 10px', color: '#64748b' }}>{row.tax_collector_name || '—'}</td>
                          <td style={{ padding: '7px 10px', color: '#64748b' }}>{row.tax_collector_phone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {step === 'complete' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }} data-testid="bulk-import-complete">
              <CheckCircle size={40} color={importResult?.skipped_count ? '#f59e0b' : '#22c55e'} style={{ margin: '0 auto 12px' }} />
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                {importResult?.skipped_count ? 'Import Completed With Skipped Rows' : 'Import Complete'}
              </div>
              <div style={{ display: 'flex', gap: '10px', margin: '14px 0 4px' }}>
                {[
                  { label: 'Imported', value: importResult?.imported_count ?? 0, bg: '#f0fdf4', color: '#166534', id: 'bulk-imported-count' },
                  { label: 'Skipped', value: importResult?.skipped_count ?? 0, bg: importResult?.skipped_count ? '#fffbeb' : '#f1f5f9', color: importResult?.skipped_count ? '#92400e' : '#334155', id: 'bulk-skipped-count' }
                ].map((s) => (
                  <div key={s.label} style={{ flex: 1, textAlign: 'center', padding: '12px 8px', background: s.bg, borderRadius: '8px' }}>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: s.color }} data-testid={s.id}>{s.value}</div>
                    <div style={{ fontSize: '11px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {importResult?.skipped_count > 0 && (
                <div style={{ marginTop: '10px', padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', maxHeight: '140px', overflowY: 'auto', textAlign: 'left' }} data-testid="bulk-skipped-rows">
                  {importResult.skipped_rows.map((r, idx) => (
                    <div key={idx} style={{ fontSize: '12px', color: '#78350f', marginBottom: '4px' }}>
                      <strong>Row {r.row}{r.jurisdiction_name ? ` (${r.jurisdiction_name})` : ''}:</strong> {r.errors.join(', ')}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'complete' && importResult?.skipped_count > 0 && (
          <div style={footerStyle}>
            <Button onClick={() => { onSuccess(); }} data-testid="bulk-import-done-btn">Done</Button>
          </div>
        )}
        {step !== 'complete' && (
          <div style={footerStyle}>
            {step === 'preview' ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setStep('upload'); setValidationResult(null); setFile(null); }}
                >
                  <ArrowLeft size={13} /> Back
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={validationResult?.valid_rows === 0 || importing}
                  isLoading={importing}
                  data-testid="confirm-import-btn"
                >
                  {importing ? 'Importing...' : `Import ${validationResult?.valid_rows || 0} Valid Row${validationResult?.valid_rows !== 1 ? 's' : ''}`}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button
                  onClick={handleValidate}
                  disabled={!file || uploading}
                  isLoading={uploading}
                  data-testid="validate-preview-btn"
                >
                  {uploading ? 'Validating...' : 'Validate & Preview'}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVUploadModal;
